// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import type { ComponentChildren } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { ApiError, api, type TermsStatusResponse } from '../api.js';
import {
  parseTermsMarkdown,
  type Inline,
  type TermsBlock,
} from '../terms-markdown.js';
import { resolveBrand } from '../brand.js';
import { BrandLockup } from './brand-lockup.js';
import '../styles/terms-modal.css';

/** Render inline runs as text / <strong> vnodes — never HTML, so the terms
 *  body cannot inject markup even if the served content were tampered with. */
function renderInlines(inlines: Inline[]): ComponentChildren {
  return inlines.map((run, i) =>
    run.bold ? <strong key={i}>{run.text}</strong> : <span key={i}>{run.text}</span>,
  );
}

/** Map a parsed terms block to a Preact vnode. Headings nest under the
 *  modal's own <h2> title (Markdown `#` → <h3>, `##` → <h4>, `###` → <h5>). */
function renderBlock(block: TermsBlock, i: number): ComponentChildren {
  switch (block.kind) {
    case 'heading': {
      const Tag = (block.level === 1 ? 'h3' : block.level === 2 ? 'h4' : 'h5') as
        | 'h3'
        | 'h4'
        | 'h5';
      return (
        <Tag key={i} class="terms-md-heading">
          {renderInlines(block.inlines)}
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p key={i} class="terms-md-p">
          {renderInlines(block.inlines)}
        </p>
      );
    case 'quote':
      return (
        <blockquote key={i} class="terms-md-quote">
          {renderInlines(block.inlines)}
        </blockquote>
      );
    case 'list':
      return (
        <ul key={i} class="terms-md-list">
          {block.items.map((item, j) => (
            <li key={j}>{renderInlines(item)}</li>
          ))}
        </ul>
      );
  }
}

interface TermsModalProps {
  /** User dismissed the modal (Cancel / close / overlay / Esc) without
   *  accepting. */
  onClose: () => void;
  /** The user accepted the current terms version — the caller should
   *  proceed with the vault-creation flow it gated on this. */
  onAccepted: () => void;
}

/**
 * COB-260 — the ProductCo Terms & Conditions acceptance modal. Self-loads
 * the current terms (`GET /auth/terms/status`) on mount so it always renders
 * — and records acceptance of — the *current* version, even if the caller's
 * cached copy was stale. Accepting posts the version it displayed; a 409
 * (version bumped since load) reloads the latest body rather than recording
 * acceptance of an unseen version.
 *
 * Mirrors the KYC modal (`kyc-modal.tsx`): overlay dialog, self-loading, Esc
 * to close. The acceptance it records is purely the write side of the fact
 * `create_vault` re-checks server-side — so this gate is advisory, not the
 * security boundary.
 */
export function TermsModal({ onClose, onAccepted }: TermsModalProps) {
  const [terms, setTerms] = useState<TermsStatusResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError(null);
    api
      .getTermsStatus()
      .then((t) => {
        if (!cancelled) setTerms(t);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Could not load the terms. Please try again.');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Esc closes the modal.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleAccept() {
    if (!terms) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await api.acceptTerms(terms.current_version);
      onAccepted();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // A version bump landed between load and accept. Reload the body so
        // the user reviews and accepts the current version, never an unseen
        // one (the server refuses to record the stale version).
        try {
          const fresh = await api.getTermsStatus();
          setTerms(fresh);
          setSubmitError(
            'These terms were updated — please review the latest version and accept again.',
          );
        } catch {
          setSubmitError(
            'These terms were updated. Please close and reopen to review the latest version.',
          );
        }
      } else {
        setSubmitError('Could not record your acceptance. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const blocks = useMemo(
    () => (terms ? parseTermsMarkdown(terms.body_md) : []),
    [terms],
  );

  // COB-394/COB-397: descriptor-driven heading. Present (the terms surface
  // serves the product-terms counterparty, e.g. Bluejade) → "<DisplayName>
  // Terms & Conditions" + the sibling-brand lockup. Absent → the exact
  // legacy "Terms & Conditions" heading, no lockup.
  const brand = resolveBrand(terms?.productco);

  return (
    <div
      class="terms-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="terms-modal-title"
      onClick={onClose}
    >
      <div class="terms-modal-card" onClick={(e) => e.stopPropagation()}>
        <div class="terms-modal-head">
          <h2 id="terms-modal-title">
            {brand.displayName ? (
              `${brand.displayName} Terms & Conditions`
            ) : (
              <>Terms &amp; Conditions</>
            )}
          </h2>
          <button
            type="button"
            class="terms-modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <BrandLockup brand={brand} class="terms-modal-lockup" />
        {loadError && <p class="terms-modal-error">{loadError}</p>}
        {!terms && !loadError && <p class="terms-modal-status">Loading terms…</p>}
        {terms && (
          <>
            <div class="terms-modal-body">{blocks.map((b, i) => renderBlock(b, i))}</div>
            {submitError && <p class="terms-modal-error">{submitError}</p>}
            <div class="terms-modal-actions">
              <button
                type="button"
                class="btn btn-secondary"
                onClick={onClose}
                disabled={submitting}
              >
                Cancel
              </button>
              <button
                type="button"
                class="btn btn-primary"
                onClick={handleAccept}
                disabled={submitting}
              >
                {submitting ? 'Recording…' : 'I have read and accept'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
